import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft, RotateCcw, LayoutList, Check, SkipForward } from "lucide-react";
import { cn } from "@/lib/utils";
import { useApp } from "@/context/AppContext";
import { getInvoices, getInvoice, getCategories, approveInvoice, rejectInvoice, updateLineItem, money } from "@/lib/apiClient";
import { ApproveCard } from "@/components/approve/ApproveCard";
import { ActionBar } from "@/components/approve/ActionBar";
import { Button } from "@/components/ui/button";
import { InstallBanner } from "@/components/InstallPrompt";
import { PushToggle } from "@/components/PushToggle";

const SWIPE = 120;
const open = (i) => i.status !== "approved" && i.status !== "rejected";

const buildQueue = (mine, review, dups, user, first) => {
  const seen = new Set();
  const out = [];
  for (const i of [...(first ? [first] : []), ...mine, ...review, ...dups]) if (open(i) && !seen.has(i.id)) { seen.add(i.id); out.push(i); }
  const rank = (i) => [first && i.id === first.id ? -1 : i.assigned_to === user ? 0 : 1, i.due_date ? new Date(i.due_date).getTime() : Infinity, new Date(i.created_at || 0).getTime()];
  return out.sort((a, b) => { const ra = rank(a), rb = rank(b); for (let k = 0; k < 3; k++) if (ra[k] !== rb[k]) return ra[k] - rb[k]; return 0; });
};

const Summary = ({ done, skipped, onRetry, navigate }) => {
  const approvedValue = done.filter((d) => d.decision === "approve").reduce((a, d) => a + (d.inv.base_total ?? d.inv.total ?? 0), 0);
  const n = (k) => done.filter((d) => d.decision === k).length;
  return (
    <div className="mx-auto max-w-md py-10 text-center animate-fade-up" data-testid="approve-summary">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><CheckCircle2 className="h-8 w-8" /></span>
      <h2 className="font-heading mt-4 text-2xl font-semibold">{done.length || skipped.length ? "All caught up" : "Nothing waiting for you"}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{done.length ? <>You approved <b className="text-foreground" data-testid="approve-summary-approved">{n("approve")}</b> ({money(approvedValue)}) and rejected <b className="text-foreground">{n("reject")}</b>{skipped.length ? <> · {skipped.length} skipped</> : null}.</> : "Every invoice that needs a human has been handled."}</p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        {skipped.length > 0 && <Button onClick={onRetry} data-testid="approve-retry-skipped"><RotateCcw className="mr-2 h-4 w-4" /> Go through the {skipped.length} skipped</Button>}
        <Button variant="outline" onClick={() => navigate("/review")} data-testid="approve-to-queue"><LayoutList className="mr-2 h-4 w-4" /> Open the full Review Queue</Button>
        <Button variant="ghost" onClick={() => navigate("/")} data-testid="approve-to-dashboard"><ArrowLeft className="mr-2 h-4 w-4" /> Dashboard</Button>
      </div>
    </div>
  );
};

export default function Approve() {
  const { user } = useApp();
  const navigate = useNavigate();
  const wantedRef = useRef(new URLSearchParams(window.location.search).get("invoice"));
  const [queue, setQueue] = useState(null);
  const [cats, setCats] = useState([]);
  const [done, setDone] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [busy, setBusy] = useState(null);
  const [leaving, setLeaving] = useState(null); // "left" | "right"
  const [dx, setDx] = useState(0);
  const touch = useRef(null);
  const total = useRef(0);

  const load = useCallback(() => {
    const wanted = wantedRef.current;
    return Promise.all([
      getInvoices({ assigned_to: user, limit: 200 }), getInvoices({ needs_review: true, limit: 200 }), getInvoices({ duplicates: true, limit: 200 }), getCategories(),
      wanted ? getInvoice(wanted).catch(() => null) : null,
    ]).then(([m, r, d, c, first]) => {
      const q = buildQueue(m, r, d, user, first); total.current = q.length; setQueue(q); setCats(c);
      if (wanted && first && !open(first)) toast.info(`${first.supplier || "That invoice"} was already ${first.status === "approved" ? "approved" : "rejected"}`);
      if (wanted) window.history.replaceState(null, "", "/approve");
    }).catch(() => { setQueue([]); toast.error("Could not load your approvals"); });
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const current = queue?.[0] || null;
  const position = total.current - (queue?.length || 0) + 1;

  const advance = (direction, record) => {
    setLeaving(direction);
    setTimeout(() => { setQueue((q) => q.slice(1)); if (record) record(); setLeaving(null); setDx(0); }, 220);
  };

  const approve = async () => {
    if (!current || busy) return;
    setBusy("approve");
    try { const inv = current; await approveInvoice(inv.id, user, null); toast.success(`${inv.supplier || "Invoice"} approved`); advance("right", () => setDone((d) => [...d, { inv, decision: "approve" }])); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setBusy(null); }
  };
  const reject = async (note) => {
    if (!current || busy) return;
    setBusy("reject");
    try { const inv = current; await rejectInvoice(inv.id, user, note); toast.error(`${inv.supplier || "Invoice"} rejected`); advance("left", () => setDone((d) => [...d, { inv, decision: "reject" }])); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
    finally { setBusy(null); }
  };
  const skip = () => { if (!current || busy) return; const inv = current; advance("left", () => setSkipped((s) => [...s, inv])); };
  const retrySkipped = () => { total.current = skipped.length; setQueue(skipped); setSkipped([]); };

  const changeCode = async (itemId, gl_code) => {
    try { const updated = await updateLineItem(current.id, itemId, gl_code, user); setQueue((q) => [updated, ...q.slice(1)]); toast.success("GL code updated & logged"); }
    catch { toast.error("Could not update code"); }
  };
  const duplicateResolved = (updated) => {
    if (updated.status === "rejected") { const inv = updated; advance("left", () => setDone((d) => [...d, { inv, decision: "reject" }])); }
    else setQueue((q) => [updated, ...q.slice(1)]);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.metaKey || e.ctrlKey) return;
      if (e.key === "a" || e.key === "A") approve();
      if (e.key === "s" || e.key === "S") skip();
      if (e.key === "r" || e.key === "R") document.querySelector('[data-testid="approve-reject-btn"]')?.click();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const onTouchStart = (e) => { if (e.target.closest("select, button, textarea, a")) return; touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, drag: false }; };
  const onTouchMove = (e) => {
    if (!touch.current) return;
    const ddx = e.touches[0].clientX - touch.current.x, ddy = e.touches[0].clientY - touch.current.y;
    if (!touch.current.drag && Math.abs(ddx) > 12 && Math.abs(ddx) > Math.abs(ddy) * 1.5) touch.current.drag = true;
    if (touch.current.drag) setDx(ddx);
  };
  const onTouchEnd = () => {
    if (!touch.current) return;
    const d = dx; touch.current = null;
    if (d > SWIPE) approve(); else if (d < -SWIPE) skip(); else setDx(0);
  };

  const remaining = queue?.length || 0;
  const pct = total.current ? Math.round(((total.current - remaining) / total.current) * 100) : 100;

  if (queue === null) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="mx-auto max-w-xl pb-32" data-testid="approve-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/review")} aria-label="Back to the queue" data-testid="approve-back" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted md:hidden"><ArrowLeft className="h-4 w-4" /></button>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">Approve</h1>
            <p className="text-sm text-muted-foreground">One invoice at a time — yours first.</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <PushToggle compact />
          {current && <span className="rounded-full bg-muted px-3 py-1 font-mono text-xs" data-testid="approve-progress">{position} of {total.current}</span>}
        </div>
      </div>
      {current && <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} data-testid="approve-progress-bar" /></div>}
      <InstallBanner />

      {!current ? <Summary done={done} skipped={skipped} onRetry={retrySkipped} navigate={navigate} /> : (
        <div className="relative">
          {queue[1] && <div className="absolute inset-x-3 -bottom-2 top-2 rounded-2xl border border-border bg-card/60" aria-hidden />}
          <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="approve-swipe-card"
               className={cn("relative rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6", leaving === "right" && "translate-x-[120%] opacity-0 transition-all duration-200", leaving === "left" && "-translate-x-[120%] opacity-0 transition-all duration-200", !leaving && dx === 0 && "transition-transform duration-200")}
               style={!leaving && dx ? { transform: `translateX(${dx}px) rotate(${dx / 40}deg)` } : undefined}>
            {dx > 40 && <span className={cn("pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-1 rounded-lg border-2 border-emerald-500 px-2 py-1 text-sm font-bold text-emerald-500", dx > SWIPE && "bg-emerald-500 text-white")}><Check className="h-4 w-4" /> {dx > SWIPE ? "Release to approve" : "Approve"}</span>}
            {dx < -40 && <span className={cn("pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-1 rounded-lg border-2 border-border px-2 py-1 text-sm font-bold text-muted-foreground", dx < -SWIPE && "bg-muted text-foreground")}><SkipForward className="h-4 w-4" /> {dx < -SWIPE ? "Release to skip" : "Skip"}</span>}
            <ApproveCard key={current.id} inv={current} cats={cats} user={user} onCode={changeCode} onDuplicateResolved={duplicateResolved} busy={!!busy} />
            <button type="button" onClick={() => navigate(`/invoice/${current.id}`)} data-testid="approve-open-full" className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline">Open the full invoice page</button>
          </div>
        </div>
      )}
      {current && <ActionBar busy={busy} onApprove={approve} onReject={reject} onSkip={skip} disabled={!!leaving} />}
    </div>
  );
}
